import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { Column, DataTable } from '../../components/ShootingData/DataTable';
import { RepetitionPerformance } from '../../types/ui/Repetition';
import { useRepetitionPerformance } from '../../hooks/useRepetitions';
import { ScenarioPerformance } from '../../types/models/scenarioPerformance';
import { EnhancedSession } from '../../types/ui/userSession';
import MetricsCard, { MetricsItem } from './MetricsCard';
import PageBreadcrumbs from '../Shared/PageBreadcrumbs';
import { formatAccuracy, formatDuration, formatHitFactor, formatHitRatio, formatRepetitionId, formatShotScore } from '../../utils/formatters';

const repetitionColumns: Column<RepetitionPerformance>[] = [
  { header: 'Rep', accessor: r => formatRepetitionId(r.id), align: 'center' },
  { header: 'Hit Score', accessor: r => formatShotScore(r.shotScore), align: 'center' },
  { header: 'Hit Ratio', accessor: r => formatHitRatio(r.hits, r.hitRatio), align: 'center' },
  { header: 'Hit Factor', accessor: r => formatHitFactor(r.hitFactor), align: 'center' },
  { header: 'Accuracy', accessor: r => formatAccuracy(r.accuracyScore), align: 'center' },
  { header: 'Duration', accessor: r => formatDuration(r.speed), align: 'center' },
];

const RepetitionDetail: React.FC = () => {
  const { sessionId, scenarioId, drillName } = useParams<{
    sessionId: string;
    scenarioId: string;
    drillName: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { session, scenario } = (location.state as {
    session: EnhancedSession;
    scenario: ScenarioPerformance;
  }) || {
    session: {
      id: Number(sessionId),
      dateLabel: '',
      timeLabel: ''
    },
    scenario: {
      speed: 0,
      accuracy_score: 0,
      shot_score: 0,
      hit_factor: 0,
      n_hits: 0,
      hits_ratio: 0,
      scenario: `Scenario ${scenarioId}`,
      id: Number(scenarioId),
      averageSpeed: 0,
      averageAccuracy: 0,
      averageScore: 0,
      userTransactionId: null
    }
  };

  const { data, isLoading, error } = useRepetitionPerformance(
    Number(sessionId),
    Number(scenarioId),
    drillName
  );

  const reps = data?.data ?? [];
  const totalReps = data?.total_count ?? reps.length;

  const metricItems: MetricsItem[] = [
    {
      label: 'Hit Score',
      value: formatShotScore(scenario.shot_score),
      icon: (
        <img
          src="/shootingData/accuracy.svg"
          onError={(e) => (e.currentTarget.style.display = 'none')}
          alt="Accuracy Icon"
          width={24}
          height={24}
          style={{
            display: 'block',
            filter: 'invert(100%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(103%) contrast(103%)',
          }}
        />
      ),
    },
    {
      label: 'Hit Ratio',
      value: formatHitRatio(scenario.n_hits, scenario.hits_ratio),
      icon: (
        <img
          src="/shootingData/ratio.svg"
          alt="Hit Ratio"
          width={24}
          height={24}
          style={{
            display: 'block',
            filter: 'invert(100%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(103%) contrast(103%)',
          }}
        />
      ),
    },
    {
      label: 'Hit Factor',
      value: formatHitFactor(scenario.hit_factor),
      icon: (
        <img
          src="/shootingData/factor.svg"
          alt="Hit Factor"
          width={24}
          height={24}
          style={{
            display: 'block',
            filter: 'invert(100%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(103%) contrast(103%)',
          }}
        />
      ),
    },
    {
      label: 'Accuracy',
      value: formatAccuracy(scenario.accuracy_score),
      icon: (
        <img
          src="/shootingData/score.svg"
          alt="Accuracy"
          width={24}
          height={24}
          style={{
            display: 'block',
            filter: 'invert(100%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(103%) contrast(103%)',
          }}
        />
      ),
    },
    {
      label: 'Duration',
      value: formatDuration(scenario.speed),
      icon: (
        <img
          src="/shootingData/time.svg"
          alt="Duration"
          width={24}
          height={24}
          style={{
            display: 'block',
            filter: 'invert(100%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(103%) contrast(103%)',
          }}
        />
      ),
    },
  ];

  if (error) {
    return (
      <Box sx={{ p: 4, backgroundColor: '#0D1117', minHeight: '100vh' }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: 'rgba(14,23,32,0)', minHeight: '100vh' }}>
      <PageBreadcrumbs
        items={[
          {
            label: `${session.dateLabel}`,
            to: {
              pathname: `/client-admin/shooting-data/${session.id}`,
              state: { session },
            },
          },
          {
            label: scenario.scenario,
            to: {
              pathname: `/client-admin/shooting-data/${session.id}/scenario/${scenario.userTransactionId}/repetitions`,
              state: { session, scenario }
            }
          }
        ]}
      />
      <Box sx={{ p: 4, backgroundColor: 'rgba(14,23,32,0)', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ ml: 1 }}>
            <Typography variant="h4" sx={{ color: '#FFF' }}>
              {scenario.scenario} Summary
            </Typography>
          </Box>
        </Box>

        <Box sx={{ maxWidth: '90%', mx: 'auto', mb: 4 }}>
          <MetricsCard items={metricItems} />
        </Box>

        <DataTable<RepetitionPerformance>
          title=""
          data={reps}
          isLoading={isLoading}
          error={undefined}
          columns={repetitionColumns}
          onRowClick={(r) =>
            navigate(`${r.drillName}/${r.repetitionNumber}`, {
              state: {
                session,
                scenario,
                repetition: r,
                totalRepetitions: totalReps
              }
            })
          }
        />
      </Box>
    </Box>
  );
};

export default RepetitionDetail;
